import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Calendar, User, Clock } from "lucide-react";
import type { BlogPost } from "@shared/schema";

function Navigation() {
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link href="/" data-testid="link-home">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-features">
            Features
          </Link>
          <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-pricing">
            Pricing
          </Link>
          <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-docs">
            Docs
          </Link>
          <Link href="/blog" className="text-sm text-foreground font-medium" data-testid="link-blog">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm" data-testid="button-login">
              Login
            </Button>
          </Link>
          <Link href="/dashboard">
            <Button size="sm" data-testid="button-signup">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-features">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-pricing">
              Pricing
            </Link>
            <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-docs">
              Docs
            </Link>
            <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-blog">
              Blog
            </Link>
            <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-contact">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-privacy">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-terms">
              Terms
            </Link>
            <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-login">
              Login
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            {new Date().getFullYear()} ReviewKit. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

function formatDate(date: Date | string | null) {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function estimateReadTime(content: string): number {
  const wordsPerMinute = 200;
  const words = content.split(/\s+/).length;
  return Math.max(1, Math.ceil(words / wordsPerMinute));
}

function FeaturedPostCard({ post }: { post: BlogPost }) {
  return (
    <Link href={`/blog/${post.slug}`} data-testid={`link-featured-post-${post.id}`}>
      <Card className="overflow-hidden hover-elevate cursor-pointer">
        <div className="grid md:grid-cols-2 gap-0">
          {post.heroImageUrl ? (
            <div 
              className="h-64 md:h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${post.heroImageUrl})` }}
            />
          ) : (
            <div className="h-64 md:h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
              <span className="text-6xl font-bold text-primary/30">R</span>
            </div>
          )}
          <CardContent className="p-8 flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="secondary">Featured</Badge>
              {post.tags?.slice(0, 2).map((tag) => (
                <Badge key={tag} variant="outline">{tag}</Badge>
              ))}
            </div>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4" data-testid="text-featured-title">
              {post.title}
            </h2>
            <p className="text-muted-foreground mb-6 line-clamp-3">
              {post.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.publishedAt)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {estimateReadTime(post.content)} min read
              </span>
            </div>
          </CardContent>
        </div>
      </Card>
    </Link>
  );
}

function BlogPostCard({ post }: { post: BlogPost }) {
  return (
    <Link href={`/blog/${post.slug}`} data-testid={`link-blog-post-${post.id}`}>
      <Card className="h-full overflow-hidden hover-elevate cursor-pointer">
        {post.heroImageUrl ? (
          <div 
            className="h-48 bg-cover bg-center"
            style={{ backgroundImage: `url(${post.heroImageUrl})` }}
          />
        ) : (
          <div className="h-48 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <span className="text-4xl font-bold text-primary/30">R</span>
          </div>
        )}
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {post.tags?.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
            ))}
          </div>
          <h3 className="text-lg font-semibold mb-2 line-clamp-2" data-testid={`text-post-title-${post.id}`}>
            {post.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {post.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {post.author}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {estimateReadTime(post.content)} min
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function BlogPostsSkeleton() {
  return (
    <>
      <div className="mb-12">
        <Skeleton className="h-80 w-full rounded-lg" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-48 w-full rounded-lg" />
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ))}
      </div>
    </>
  );
}

export default function Blog() {
  const { data: posts, isLoading, error } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog"],
  });

  const featuredPost = posts?.[0];
  const otherPosts = posts?.slice(1) || [];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main>
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="text-blog-headline">
                Blog
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Tips, strategies, and insights to help local service businesses build their online reputation and get more Google reviews.
              </p>
            </div>

            {isLoading && <BlogPostsSkeleton />}

            {error && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Failed to load blog posts. Please try again later.</p>
              </div>
            )}

            {posts && posts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No blog posts yet. Check back soon!</p>
              </div>
            )}

            {featuredPost && (
              <div className="mb-12">
                <FeaturedPostCard post={featuredPost} />
              </div>
            )}

            {otherPosts.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {otherPosts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-semibold mb-4" data-testid="text-cta-headline">
              Ready to grow your online reputation?
            </h2>
            <p className="text-muted-foreground mb-6">
              Start collecting more Google reviews today with ReviewKit.
            </p>
            <Link href="/dashboard">
              <Button size="lg" className="gap-2" data-testid="button-blog-cta">
                Get Started Free
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
